import React, { useState } from 'react';
import { api } from '../../api/client';
import ReferenceContactPicker from './ReferenceContactPicker';

import { ta } from '../../i18n/adminT';
const emptyForm = {
  nome: '', cognome: '', ditta: '', nfc_uid: '', tipo_documento: '',
  numero_documento: '', email: '', send_checkin_email: true, references: [],
};

function formFromUser(user) {
  if (!user) return emptyForm;
  return {
    nome: user.nome,
    cognome: user.cognome,
    ditta: user.ditta,
    nfc_uid: user.nfc_uid || '',
    tipo_documento: user.tipo_documento || '',
    numero_documento: user.numero_documento || '',
    email: user.email || '',
    send_checkin_email: user.send_checkin_email !== false,
    references: user.references || [],
  };
}

// Popup per creare (user = null) o modificare un utente esterno.
// Non si chiude cliccando fuori dal riquadro: un clic accidentale
// farebbe perdere i dati appena digitati.
export default function ExternalUserFormModal({ user, onClose, onSaved }) {
  const isEditing = Boolean(user);
  const [form, setForm] = useState(() => formFromUser(user));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nome.trim() || !form.cognome.trim() || !form.ditta.trim()) {
      setError(ta('Nome, cognome e ditta sono obbligatori.'));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, referenceContactIds: form.references.map((r) => r.id) };
      if (isEditing) {
        await api.updateExternalUser(user.id, payload);
      } else {
        await api.createExternalUser(payload);
      }
      await onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const addReference = (rc) => setForm((f) => ({ ...f, references: [...f.references, rc] }));
  const removeReference = (id) => setForm((f) => ({ ...f, references: f.references.filter((r) => r.id !== id) }));

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-card" style={{ maxWidth: 520 }}>
          <h2 style={{ marginBottom: 14 }}>{isEditing ? ta('Modifica utente esterno') : ta('Nuovo utente esterno')}</h2>
          {error && <div className="form-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="field-row">
              <div className="field">
                <label>{ta('Nome *')}</label>
                <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} autoFocus />
              </div>
              <div className="field">
                <label>{ta('Cognome *')}</label>
                <input type="text" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label>{ta('Ditta *')}</label>
              <input type="text" value={form.ditta} onChange={(e) => setForm({ ...form, ditta: e.target.value })} />
            </div>
            <div className="field-row">
              <div className="field">
                <label>{ta('Tipo documento')} <span className="optional-tag">{ta('(opzionale)')}</span></label>
                <select value={form.tipo_documento} onChange={(e) => setForm({ ...form, tipo_documento: e.target.value })}>
                  <option value="">{ta('Seleziona…')}</option>
                  <option value="carta_identita">{ta('Carta d\'identità')}</option>
                  <option value="patente">{ta('Patente')}</option>
                  <option value="passaporto">{ta('Passaporto')}</option>
                </select>
              </div>
              <div className="field">
                <label>{ta('Numero documento')} <span className="optional-tag">{ta('(opzionale)')}</span></label>
                <input type="text" value={form.numero_documento} onChange={(e) => setForm({ ...form, numero_documento: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label>{ta('Badge NFC')} <span className="optional-tag">{ta('(opzionale — avvicina il badge al lettore collegato a questo computer, oppure incollane l\'ID)')}</span></label>
              <input type="text" className="mono" value={form.nfc_uid} onChange={(e) => setForm({ ...form, nfc_uid: e.target.value })} placeholder={ta('Es. 04A3F2...')} />
            </div>
            <div className="field">
              <label>{ta('Email')} <span className="optional-tag">{ta('(opzionale)')}</span></label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={ta('nome@esempio.it')} />
              <span className="field-hint">
                {ta('Se impostata, e se l\'invio è attivo in Impostazioni → Email di benvenuto, questa persona riceve privacy e sicurezza via email a ogni check-in con il badge.')}
              </span>
            </div>
            {form.email.trim() && (
              <label className="checkbox-row">
                <input type="checkbox" checked={form.send_checkin_email} onChange={(e) => setForm({ ...form, send_checkin_email: e.target.checked })} />
                <span>{ta('Invia l\'email a questa persona (disattivalo per escluderla pur mantenendo l\'indirizzo salvato)')}</span>
              </label>
            )}

            <div className="field">
              <label>{ta('Persone di riferimento')} <span className="optional-tag">{ta('(opzionale)')}</span></label>
              <span className="field-hint" style={{ display: 'block', marginBottom: 8 }}>
                {ta('Se ne colleghi una sola, viene avvisata via email a ogni check-in badge, in automatico. Se ne colleghi più di una, dopo la firma (o subito, se la firma non è richiesta) la persona vede un elenco e sceglie lei stessa chi avvisare — anche "nessuno".')}
              </span>

              {form.references.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  {form.references.map((r) => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', border: '1px solid var(--border-hair)', borderRadius: 'var(--radius-control)' }}>
                      <span>{r.nome} {r.cognome}</span>
                      <button type="button" className="link-button" onClick={() => removeReference(r.id)}>{ta('Rimuovi')}</button>
                    </div>
                  ))}
                </div>
              )}

              <button type="button" className="btn btn-ghost" onClick={() => setPickerOpen(true)} style={{ width: 'auto', padding: '8px 16px' }}>
                {ta('+ Aggiungi persona di riferimento')}
              </button>
            </div>

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>{ta('Annulla')}</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? ta('Salvataggio…') : isEditing ? ta('Salva modifiche') : ta('Crea utente esterno')}
              </button>
            </div>
          </form>
          {!isEditing && (
            <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: 12 }}>
              {ta('Il codice di riserva viene generato automaticamente.')}
            </p>
          )}
        </div>
      </div>

      {pickerOpen && (
        <ReferenceContactPicker
          excludeIds={form.references.map((r) => r.id)}
          onPick={addReference}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}
